import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import Swal, { SweetAlertResult } from 'sweetalert2';

import { NSEBillConstants } from '@ruby/configs/ebill.constants';
import { EBillNotesDialogComponent } from '@ruby/shared/dialogs/ebill/ebill-notes/ebill-notes.dialog';
import { IEBillProfile, IEBillSearchTypes } from '@ruby/shared/models/ebill/ebil.interface';

@Component({
  selector: 'ng-ebill-header',
  templateUrl: './ebill-header.component.html',
  styleUrls: ['./ebill-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EBillHeaderComponent implements OnChanges {
  @Input() profiles!: Array<IEBillProfile>;
  @Input() profile!: IEBillProfile;
  @Input() isAdmin!: boolean;
  @Input() loading!: boolean;
  @Input() impersonateId?: string;
  @Input() isValidRacf?: { message?: string; value: boolean };
  @Output() validateRacf = new EventEmitter<string>();
  @Output() selectedProfile = new EventEmitter<IEBillProfile>();
  @Output() alternateUser = new EventEmitter<string>();
  @Output() search = new EventEmitter<{ searchType: IEBillSearchTypes; value: string }>();
  searchTypes: Array<IEBillSearchTypes> = [];
  form: UntypedFormGroup = new UntypedFormGroup({
    searchType: new UntypedFormControl('', [this.searchTypeValidator()]),
    value: new UntypedFormControl('')
  });
  checkUserRacf = new Subject<{ message?: string; value: boolean }>();

  constructor(private dialog: MatDialog) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isAdmin) {
      if (changes.isAdmin.currentValue) {
        this.searchTypes = NSEBillConstants.searchTypes;
      } else {
        this.searchTypes = NSEBillConstants.searchTypes.filter((item: IEBillSearchTypes) => !item.customer);
      }
    }
    if (changes.isValidRacf && changes.isValidRacf.currentValue) {
      this.checkUserRacf.next(changes.isValidRacf.currentValue);
    }
  }

  /**
   * open AlternateDialog
   *
   * @summary open eBill user lookup
   * @returns void
   */
  openAlternateDialog(): void {
    Swal.fire({
      ...NSEBillConstants.userLookupSwlConfig,
      inputValue: `${ this.impersonateId || '' }`,
      showLoaderOnConfirm: true,
      preConfirm: (userRacf: string) => this.validateAlternateId(userRacf)
    }).then((result: SweetAlertResult) => {
      if (result.isConfirmed && this.impersonateId !== result.value) {
        this.impersonateId = result.value;
        this.alternateUser.emit(result.value);
      }
    });
  }

  /**
   * open help dialog
   *
   * @summary open help dialog
   * @returns void
   */
  openHelpDialog(): void {
    this.dialog.open(EBillNotesDialogComponent, {
      data: { title: 'Hints for Entering Search Criteria', isAdmin: this.isAdmin, type: 'Help' },
      width: '50%'
    });
  }

  private searchTypeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const formGroup = control.parent! as UntypedFormGroup;
      if (formGroup) {
        if (control.value) {
          formGroup.controls.value.enable();
          formGroup.controls.value.setValidators([Validators.required, Validators.pattern(control.value.pattern)]);
          formGroup.controls.value.updateValueAndValidity();
        } else {
          formGroup.controls.value.reset();
          formGroup.controls.value.disable();
          formGroup.markAsPristine();
        }
      }
      return {};
    };
  }

  private validateAlternateId(userRacf: string): Observable<string | boolean> {
    if (this.impersonateId !== userRacf) {
      this.validateRacf.emit(userRacf);
      return this.checkUserRacf.pipe(
        take(1),
        map((res: { value: boolean; message?: string }) => {
          if (res.value) {
            return userRacf;
          } else {
            Swal.showValidationMessage(`User Not Found: ${ res.message }`);
            return false;
          }
        })
      );
    } else {
      return of(userRacf);
    }
  }
}
